/*
 *  -----------------------------------------------------------------------
 *    <copyright file="App.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useApolloClient } from '@apollo/client/react';

import ToastNotifications, { ToastMessageType } from '@components-clean/core/toast-notifications';

import { useWindowEventListener } from '@hooks-clean/use-window-event-listener';
import DefaultHighchartOptions from '@utils-clean/highchart-options/default-highchart-options';
import { OPRALegendScrollPlugin } from '@utils-clean/highchart-options/legend-scrollbar-plugin';
import LocalStorage from '@utils-clean/local-storage-service';
import Highcharts from 'highcharts/esm/highcharts.js';
import * as HighMaps from 'highcharts/highmaps';
import * as HighStocks from 'highcharts/highstock';
import markerClusters from "highcharts/modules/marker-clusters";
import { createBrowserHistory } from 'history';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { IIdleTimer, useIdleTimer } from 'react-idle-timer';
import { BrowserRouter } from 'react-router';

import { SWRConfig } from 'swr';
import { FeatureContext } from 'contexts/feature-context';
import { UserContext } from 'contexts/user-context';
import ErrorBoundary from 'hoc/error-boundary';
import { useBoolean, useLocalState, useLogger } from 'hooks';
import doLogout from 'utils/auth.service';
import NotificationService from 'utils/notification';
import Loading from 'views/components/core/loading';
import NotificationSnackbar from 'views/components/core/notification-snackbar';
import Router from '@utils/router/router';

import './App.scss';
import { KendoTheme } from '@config/theme';

HighMaps.setOptions(DefaultHighchartOptions);
Highcharts.setOptions(DefaultHighchartOptions);
HighStocks.setOptions(DefaultHighchartOptions);
markerClusters.setOptions(DefaultHighchartOptions);
OPRALegendScrollPlugin(Highcharts);

const history = createBrowserHistory();

const App = () => {
    const userContext = useContext(UserContext);
    const { state } = useLocalState();
    const client = useApolloClient();
    const Logger = useLogger();
    const showNotification = useBoolean(false);
    const [notificationMessage, setNotificationMessage] = useState<{ text: string; type: ToastMessageType } | null>(
        null,
    );
    const [timeoutState, setTimeoutState] = useState<number | null>(null);
    const idleTimerRef = useRef<IIdleTimer | null>(null);

    // To allow external access to feature flags:
    // These can be toggled from the console:
    //
    //    -- returns an array of feature names
    //    window.featureContext.features()
    //
    //    -- turns a feature on/off
    //    window.featureContext.setFeature(featureName, on/off)
    //
    //    -- returns true if the feature is turned on, else false
    //    window.featureContext.hasFeature(featureName)
    // @ts-ignore
    window.featureContext = useContext(FeatureContext);

    const onIdle = () => {
        if (userContext.user) {
            doLogout(client, userContext, 'IdleTimeout');
        }
    };

    const onAction = () => {
        if (!userContext.user) {
            return;
        }
        LocalStorage.setItem(userContext.user, 'LastActive', Date.now().toString());
    };

    const idleTimer = useIdleTimer({
        timeout: timeoutState ?? undefined,
        onIdle,
        onAction,
        ref: idleTimerRef,
        debounce: 250,
        startManually: true,
    });

    useEffect(() => {
        if (userContext.user) {
            let minutes: number | undefined;
            const org = userContext.user?.organisation;
            if (
                org &&
                org.security &&
                typeof (org.security as { userIdleTimeoutMinutes?: unknown }).userIdleTimeoutMinutes === 'number'
            ) {
                minutes = (org.security as { userIdleTimeoutMinutes: number }).userIdleTimeoutMinutes;
            }

            if (minutes) setTimeoutState(minutes * 1000 * 60);

            if (!window.location.pathname) history.push('/home');
        }
    }, [userContext.user]);

    useEffect(() => {
        if (timeoutState) {
            idleTimer.reset();
            idleTimer.start();
        }
    }, [timeoutState]);

    // Monitor local storage changes
    useWindowEventListener('storage', (event: StorageEvent) => {
        if (!userContext.user?.id) return;

        // If LastActive is set, reset the idle timer
        if (event.key === `${userContext.user?.id}_LastActive`) {
            idleTimer.reset();
            idleTimer.start();
        }
    });

    const [currentTheme, setCurrentTheme] = useState<KendoTheme>('legacy');
    useEffect(() => {
        if (currentTheme !== 'legacy') return;
        const subscription = NotificationService.onUpdateNotification().subscribe(
            (message: { text: string; type: ToastMessageType } | string) => {
                Logger.log('GOT SUBSCRIPTION MESSAGE: ', message);
                // The object is set here because sometimes the same message should be shown
                if (typeof message === 'string') {
                    setNotificationMessage({ text: message, type: 'none' });
                } else {
                    setNotificationMessage(message);
                }
                showNotification.setTrue();
            },
        );
        return () => {
            subscription.unsubscribe();
        };
    }, [currentTheme]);

    const [previousTheme, setPreviousTheme] = useState<KendoTheme | null>(null);

    const loadCSS = (currTheme: KendoTheme, prevTheme: KendoTheme | null) => {
        setPreviousTheme(currTheme);

        const { head } = document;

        setTimeout(() => {
            // Remove previous theme if it exists
            const linkElement = document.querySelector(`link[href='/themes/${prevTheme}.css']`);

            if (linkElement) {
                linkElement.remove();
            }
        }, 500);

        // Create and append new theme's link
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = `/themes/${currTheme}.css`;
        head.appendChild(link);
    };

    useEffect(() => {
        loadCSS(currentTheme, previousTheme);
    }, [currentTheme]);

    const organisationKey =
        (state?.selectedOrganisation?.organisationKey as string) ?? userContext.user?.organisationKey;
    const study = state?.selectedStudy;
    const legacyDataset = state?.selectedDatasetDate;
    const cm2Dataset = state?.selectedCM2Dataset;

    return (
        <div>
            {userContext.token && !userContext.user && <Loading overlay />}
            <BrowserRouter>
                <ErrorBoundary history={history} layoutError>
                    {currentTheme !== 'legacy' ? (
                        <ToastNotifications autoHideDuration={3000} />
                    ) : (
                        <NotificationSnackbar
                            open={showNotification.value}
                            message={notificationMessage?.text}
                            onClose={() => {
                                showNotification.setFalse();
                                setNotificationMessage(null);
                            }}
                            position={{
                                horizontal: 'right',
                                vertical: 'bottom',
                            }}
                            autoHideDuration={5000}
                        />
                    )}
                    <SWRConfig
                        value={{
                            revalidateOnFocus: false,
                            revalidateOnMount: true,
                        }}
                    >
                        {userContext.token && !userContext.user ? null : (
                            <Router
                                user={userContext.user}
                                organisationKey={organisationKey}
                                study={study}
                                legacyDataset={legacyDataset}
                                cm2Dataset={cm2Dataset}
                                token={userContext.token}
                                history={history}
                                currentTheme={currentTheme}
                                setCurrentTheme={setCurrentTheme}
                            />
                        )}
                    </SWRConfig>
                </ErrorBoundary>
            </BrowserRouter>
        </div>
    );
};

export default App;
